import * as paymentApi from '@/api/payment';
import type { Product } from '@/api/payment';
import { Button, Card, Modal, Radio, Spin, message } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createElement, init } from '@airwallex/components-sdk';

interface PaymentDialogProps {
    visible: boolean;
    onClose: () => void;
}

type Step = 'select_product' | 'payment';

const CONTAINER_ID = 'airwallex-dropin-container';

const PaymentDialog: React.FC<PaymentDialogProps> = ({
    visible,
    onClose,
}) => {
    const { t } = useTranslation('common');
    const [step, setStep] = useState<Step>('select_product');
    const [products, setProducts] = useState<Product[]>([]);
    const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [creatingPayment, setCreatingPayment] = useState(false);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const dropinContainerRef = useRef<HTMLDivElement>(null);
    const initializedRef = useRef(false);
    const paymentDataRef = useRef<{ intentId: string; clientSecret: string; currency: string } | null>(null);

    const fetchProducts = useCallback(async () => {
        setLoadingProducts(true);
        try {
            const res = await paymentApi.getProducts();
            if (res?.ok) {
                const activeProducts = (res.data ?? []).filter(p => p.is_active);
                setProducts(activeProducts);
            } else {
                message.error(res?.message ?? t('payment.load_products_failed'));
            }
        } catch {
            message.error(t('payment.load_products_failed'));
        } finally {
            setLoadingProducts(false);
        }
    }, [t]);

    useEffect(() => {
        if (visible) {
            setStep('select_product');
            setSelectedProductId(null);
            fetchProducts();
        } else {
            setProducts([]);
            setSelectedProductId(null);
        }
    }, [visible, fetchProducts]);

    useEffect(() => {
        if (step !== 'payment' || !paymentDataRef.current) return;

        const domElement = document.getElementById(CONTAINER_ID);
        if (!domElement) return;

        const onReady = (): void => {
            setPaymentLoading(false);
        };

        const onSuccess = (): void => {
            message.success(t('payment.payment_success'));
            paymentDataRef.current = null;
            if (dropinContainerRef.current) {
                dropinContainerRef.current.innerHTML = '';
            }
            onClose();
        };

        const onError = (event: CustomEvent): void => {
            const { error } = event.detail;
            message.error(error?.message ?? t('payment.payment_failed'));
            setPaymentLoading(false);
        };

        domElement.addEventListener('onReady', onReady as EventListener);
        domElement.addEventListener('onSuccess', onSuccess as EventListener);
        domElement.addEventListener('onError', onError as EventListener);

        const loadDropInElement = async () => {
            try {
                if (!initializedRef.current) {
                    await init({
                        env: 'demo',
                        enabledElements: ['payments'],
                    });
                    initializedRef.current = true;
                }

                const { intentId, clientSecret, currency } = paymentDataRef.current!;
                const element = await createElement('dropIn', {
                    intent_id: intentId,
                    client_secret: clientSecret,
                    currency,
                });

                element?.mount(CONTAINER_ID);
            } catch (error) {
                console.error('Failed to initialize Airwallex Drop-in', error);
                message.error(t('payment.init_payment_failed'));
                setPaymentLoading(false);
            }
        };

        loadDropInElement();

        return () => {
            domElement.removeEventListener('onReady', onReady as EventListener);
            domElement.removeEventListener('onSuccess', onSuccess as EventListener);
            domElement.removeEventListener('onError', onError as EventListener);
        };
    }, [step, t, onClose]);

    const handleCreatePayment = async () => {
        if (selectedProductId === null) {
            message.warning(t('payment.select_product_first'));
            return;
        }

        setCreatingPayment(true);
        try {
            const res = await paymentApi.createPayment(selectedProductId);
            if (res?.ok && res.data?.client_secret) {
                paymentDataRef.current = {
                    intentId: res.data.payment_intent_id,
                    clientSecret: res.data.client_secret,
                    currency: res.data.currency,
                };
                setStep('payment');
                setPaymentLoading(true);
            } else {
                message.error(res?.message ?? t('payment.create_payment_failed'));
            }
        } catch {
            message.error(t('payment.create_payment_failed'));
        } finally {
            setCreatingPayment(false);
        }
    };

    const handleClose = () => {
        paymentDataRef.current = null;
        if (dropinContainerRef.current) {
            dropinContainerRef.current.innerHTML = '';
        }
        onClose();
    };

    const selectedProduct = products.find(p => p.id === selectedProductId);

    return (
        <Modal
            title={t('payment.title')}
            open={visible}
            onCancel={handleClose}
            width={560}
            destroyOnClose
            footer={step === 'select_product' ? [
                <Button key="cancel" onClick={handleClose}>
                    {t('payment.cancel')}
                </Button>,
                <Button
                    key="pay"
                    type="primary"
                    loading={creatingPayment}
                    disabled={selectedProductId === null}
                    onClick={handleCreatePayment}
                >
                    {t('payment.pay_now')}
                </Button>,
            ] : null}
        >
            {step === 'select_product' && (
                <Spin spinning={loadingProducts}>
                    {products.length === 0 && !loadingProducts ? (
                        <div className="text-center py-8 text-gray-400">
                            {t('payment.no_products')}
                        </div>
                    ) : (
                        <Radio.Group
                            value={selectedProductId}
                            onChange={e => setSelectedProductId(e.target.value)}
                            className="flex flex-col gap-3 w-full"
                        >
                            {products.map(product => (
                                <Card
                                    key={product.id}
                                    size="small"
                                    className={`cursor-pointer transition-all ${
                                        selectedProductId === product.id
                                            ? 'border-primary shadow-sm'
                                            : ''
                                    }`}
                                    onClick={() => setSelectedProductId(product.id)}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Radio value={product.id} />
                                            <div>
                                                <div className="font-medium">{product.name}</div>
                                                {product.description && (
                                                    <div className="text-sm text-gray-500">
                                                        {product.description}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-lg font-semibold text-primary">
                                            {product.currency} {product.amount.toFixed(2)}
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </Radio.Group>
                    )}
                </Spin>
            )}

            {step === 'payment' && (
                <div>
                    {selectedProduct && (
                        <div className="mb-4 p-3 bg-gray-50 rounded">
                            <div className="font-medium">{selectedProduct.name}</div>
                            <div className="text-sm text-gray-500">
                                {selectedProduct.currency} {selectedProduct.amount.toFixed(2)}
                            </div>
                        </div>
                    )}
                    <Spin spinning={paymentLoading} tip={t('payment.loading_payment')}>
                        <div
                            id={CONTAINER_ID}
                            ref={dropinContainerRef}
                            className="min-h-[200px]"
                        />
                    </Spin>
                </div>
            )}
        </Modal>
    );
};

export default PaymentDialog;
